import React, { useState, useEffect } from "react";

const CatalogDownloadPage = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchCatalogs();
  }, []);

  const fetchCatalogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/api/catalogs"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch catalogs");
      }

      const data = await response.json();
      setCatalogs(data);
    } catch (err) {
      console.error("Error fetching catalogs:", err);
      setError("無法載入型錄資料，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (catalog) => {
    if (!catalog.file_path) {
      alert("此型錄暫無檔案可下載");
      return;
    }

    // 防止重複下載
    if (downloadingId === catalog.id) {
      return;
    }

    setDownloadingId(catalog.id);

    try {
      // 使用新的專用下載API，提供更好的性能
      const downloadUrl = `${process.env.REACT_APP_API_URL}/api/download/${catalog.id}`;

      // 先檢查API是否可用
      const response = await fetch(downloadUrl, { method: "HEAD" });

      if (!response.ok) {
        throw new Error("檔案不可用或已被移除");
      }

      // 獲取檔案名稱（從Content-Disposition標頭或使用預設值）
      let fileName = `${catalog.title}.pdf`;
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
        }
      }

      // 創建隱藏的下載連結，直接連到API
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      link.target = "_blank"; // 在新標籤頁開啟，避免阻塞主頁面

      // 隱藏連結並觸發下載
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);

      // 可選：顯示下載成功提示
      console.log(`開始下載: ${fileName}`);
    } catch (error) {
      console.error("下載失敗:", error);
      alert(`檔案下載失敗：${error.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="container mx-auto py-24">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold mb-6">型錄下載</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          下載最新的產品型錄與技術資料，掌握第一手資訊，讓您的設計更加專業
        </p>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-lg text-gray-600">載入中...</span>
        </div>
      )}

      {error && (
        <div className="text-center py-20">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchCatalogs}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              重新載入
            </button>
          </div>
        </div>
      )}

      {!loading && !error && catalogs.length === 0 && (
        <div className="text-center py-20">
          <div className="text-gray-500">
            <svg
              className="mx-auto h-24 w-24 text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-xl">目前暫無型錄資料</p>
            <p className="text-gray-400 mt-2">請稍後再來查看</p>
          </div>
        </div>
      )}

      {!loading && !error && catalogs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {catalogs.map((catalog) => (
            <div
              key={catalog.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-blue-200 transform hover:-translate-y-2"
            >
              {/* 卡片頭部 */}
              <div className="relative h-60 bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
                <div>
                  <svg
                    className="w-20 h-20 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-white/80 backdrop-blur-sm text-xs px-2 py-1 rounded-full text-gray-600">
                    {new Date(catalog.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* 卡片內容 */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                  {catalog.title}
                </h3>

                {catalog.description && (
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {catalog.description}
                  </p>
                )}

                <button
                  onClick={() => handleDownload(catalog)}
                  disabled={!catalog.file_path || downloadingId === catalog.id}
                  className={`w-full py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl ${
                    !catalog.file_path
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : downloadingId === catalog.id
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-green-600 text-white hover:from-blue-700 hover:to-green-700 transform hover:scale-105"
                  }`}
                >
                  {downloadingId === catalog.id ? (
                    <>
                      <svg
                        className="animate-spin w-5 h-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      下載中...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {catalog.file_path ? "立即下載" : "暫無檔案"}
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogDownloadPage;
